/**
 * Status presentation. Maps the eight quote statuses (spec §6) to human labels
 * and a semantic tone used by the StatusPill.
 *
 * Build decision: `--warning` exists in the token system but is intentionally
 * NOT used. The "caution" tone (expired) maps to the `danger` palette; neutral
 * lifecycle states map to `info`. Tones here are palette keys, not raw colours.
 */

import type { QuoteStatus } from "@/types";

export type StatusTone = "neutral" | "info" | "info-soft" | "success" | "danger" | "accent";

interface StatusMeta {
  label: string;
  tone: StatusTone;
}

const STATUS_META: Record<QuoteStatus, StatusMeta> = {
  draft: { label: "Draft", tone: "info-soft" },
  sent: { label: "Published", tone: "info" },
  viewed: { label: "Viewed", tone: "info" },
  accepted: { label: "Accepted", tone: "success" },
  converted: { label: "Converted", tone: "accent" },
  declined: { label: "Declined", tone: "danger" },
  // expired + withdrawn read as inactive/grey (no --warning in components).
  expired: { label: "Expired", tone: "neutral" },
  withdrawn: { label: "Withdrawn", tone: "neutral" },
};

export function statusLabel(status: QuoteStatus): string {
  return STATUS_META[status]?.label ?? status;
}

export function statusTone(status: QuoteStatus): StatusTone {
  return STATUS_META[status]?.tone ?? "neutral";
}

/** Terminal statuses can't be published/withdrawn (spec §6). */
export function isTerminal(status: QuoteStatus): boolean {
  return (
    status === "accepted" ||
    status === "converted" ||
    status === "declined" ||
    status === "withdrawn"
  );
}
